// Type-only: AIInsightsModal.utils imports this module at runtime, so a value import here
// would create a require cycle.
import type { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';

// Must match the agent's a2ui_surfaces.py VISITIQ_CATALOG_ID.
export const VISITIQ_CATALOG_ID = 'visitiq://components/v1';

const A2UI_VERSION = 'v0.9';

// Raw generate_dynamic_ui tool input as persisted and returned by the API in
// ConversationMessage.a2ui (already camelCased by the API read path). `surfaceId` is the
// server-derived form id ("form:<toolUseId>"); the API always sets it.
export interface A2uiSurfacePayload {
  components: unknown[];
  dataModel?: Record<string, unknown> | null;
  surfaceId: string;
}

// Rebuilds the A2UI v0.9 operations envelope from a persisted surface payload. Exact mirror
// of the agent's a2ui_surfaces.build_operations_envelope: include updateDataModel whenever the
// data model is present (an empty object is a valid model that initializes/clears the surface
// state), skipping it only for an absent (null/undefined) model — ordering matters for the
// client processor.
export const buildOperationsEnvelope = ({ components, dataModel, surfaceId }: A2uiSurfacePayload): A2uiOperation[] => [
  { version: A2UI_VERSION, createSurface: { surfaceId, catalogId: VISITIQ_CATALOG_ID } },
  ...(dataModel != null
    ? [{ version: A2UI_VERSION, updateDataModel: { surfaceId, path: '/', value: dataModel } }]
    : []),
  { version: A2UI_VERSION, updateComponents: { surfaceId, components } },
];

// Overlays a submission's context values onto a surface's operations so a reloaded, already
// submitted form renders with the values the user entered. Merges over the root
// updateDataModel value, or inserts the op after createSurface when the surface had no
// initial data model. Returns a new array; never mutates the input operations.
export const mergeSubmittedValues = (
  operations: A2uiOperation[],
  surfaceId: string,
  context: Record<string, unknown>,
): A2uiOperation[] => {
  const dataModelIndex = operations.findIndex((op) => {
    const update = op.updateDataModel as { surfaceId?: string; path?: string } | undefined;
    return update?.surfaceId === surfaceId && update?.path === '/';
  });

  if (dataModelIndex >= 0) {
    const existing = operations[dataModelIndex].updateDataModel as { value?: Record<string, unknown> };
    const merged = {
      ...operations[dataModelIndex],
      updateDataModel: { ...existing, value: { ...existing.value, ...context } },
    };
    return [...operations.slice(0, dataModelIndex), merged, ...operations.slice(dataModelIndex + 1)];
  }

  const createIndex = operations.findIndex(
    (op) => (op.createSurface as { surfaceId?: string } | undefined)?.surfaceId === surfaceId,
  );
  const insertAt = createIndex >= 0 ? createIndex + 1 : 0;
  const dataModelOp = { version: A2UI_VERSION, updateDataModel: { surfaceId, path: '/', value: context } };
  return [...operations.slice(0, insertAt), dataModelOp, ...operations.slice(insertAt)];
};
